import { useState } from "react";
import { Chip } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import Download from "@mui/icons-material/Download";
import {
  BooleanInput,
  Datagrid,
  DateField,
  FunctionField,
  List,
  NumberField,
  SelectInput,
  useRecordContext,
  useRefresh,
} from "react-admin";
import { useCurrentDrivingSchoolId } from "../../../hooks/useCurrentDrivingSchoolId";
import { EmptyState } from "../../../misc/EmptyState";
import { downloadDATEVExport } from "../../../api/datev.api";
import type { DATEVExportRunRecord } from "../../../providers/datevProvider";
import { DATEV_MODE_LABELS } from "./datevLabels";

export function DATEVExportRuns() {
  const drivingSchoolId = useCurrentDrivingSchoolId();
  return (
    <List
      resource="datevExportRuns"
      title=" "
      filter={{ drivingSchoolId }}
      storeKey={`DATEVExports@${drivingSchoolId}`}
      sort={{ field: "created_at", order: "DESC" }}
      exporter={false}
      empty={<EmptyState noEntriesText="Noch keine DATEV-Exporte vorhanden." />}
      filters={[
        <SelectInput
          key="mode"
          source="mode"
          label="Exportart"
          choices={Object.entries(DATEV_MODE_LABELS).map(([id, name]) => ({ id, name }))}
          alwaysOn
        />,
        <SelectInput
          key="status"
          source="status"
          label="Status"
          choices={[
            { id: "SUCCEEDED", name: "Erstellt" },
            { id: "RUNNING", name: "In Erstellung" },
            { id: "FAILED", name: "Fehlgeschlagen" },
          ]}
        />,
        <BooleanInput key="customer_downloaded" source="customer_downloaded" label="Bereits heruntergeladen" />,
        <BooleanInput key="is_faulty" source="is_faulty" label="Mit Prüfhinweis" />,
      ]}
    >
      <Datagrid
        bulkActionButtons={false}
        rowClick={false}
        empty={
          <EmptyState
            noEntriesText="Noch keine DATEV-Exporte vorhanden."
            noMatchingEntriesText="Keine passenden DATEV-Exporte gefunden."
          />
        }
      >
        <FunctionField
          source="mode"
          label="Art"
          sortable={false}
          render={(r: DATEVExportRunRecord) => DATEV_MODE_LABELS[r.mode]}
        />
        <DateField source="start_date" label="Von" locales="de-DE" />
        <DateField source="end_date" label="Bis" locales="de-DE" sortable={false} />
        <DateField source="created_at" label="Erstellt" locales="de-DE" showTime />
        <NumberField source="row_count" label="Buchungen" sortable={false} />
        <FunctionField
          label="Status"
          sortable={false}
          render={(r: DATEVExportRunRecord) => (
            <Chip
              size="small"
              color={r.is_faulty || r.status === "FAILED" ? "warning" : "default"}
              label={
                r.is_faulty
                  ? "Prüfung erforderlich"
                  : r.status === "SUCCEEDED"
                    ? "Erstellt"
                    : r.status === "RUNNING"
                      ? "In Erstellung"
                      : "Fehlgeschlagen"
              }
            />
          )}
        />
        <FunctionField
          label="Download"
          sortable={false}
          render={(r: DATEVExportRunRecord) =>
            r.customer_downloaded ? "Bereits heruntergeladen" : "Noch nicht heruntergeladen"
          }
        />
        <DATEVDownloadButton />
      </Datagrid>
    </List>
  );
}

function DATEVDownloadButton() {
  const run = useRecordContext<DATEVExportRunRecord>();
  const refresh = useRefresh();
  const [loading, setLoading] = useState(false);
  if (!run?.downloadable) return null;
  async function download() {
    setLoading(true);
    await runSafely(
      async () => {
        const blob = await downloadDATEVExport(run.id);
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `EXTF_${run.start_date}_${run.end_date}_${run.id}.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 1000);
        refresh();
      },
      { errorMessage: "Die DATEV-Datei konnte nicht heruntergeladen werden." },
    );
    setLoading(false);
  }
  return (
    <LoadingButton loading={loading} onClick={download} startIcon={<Download />}>
      CSV herunterladen
    </LoadingButton>
  );
}
